import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { X, Plus, Trash } from 'lucide-react';
import { Challenge } from '../../types/challenges';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '../../lib/firebase';

interface EditChallengeModalProps {
  challenge: Challenge;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

export function EditChallengeModal({ 
  challenge,
  isOpen, 
  onClose,
  onUpdate
}: EditChallengeModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null);
  const initialRenderRef = useRef(true);
  const previousChallengeRef = useRef(challenge);

  // Initialize form data with memoized deep clone of challenge
  const initialFormData = useMemo(() => ({
    ...challenge,
    requirements: {
      flour: [...challenge.requirements.flour],
      equipment: [...challenge.requirements.equipment],
      technique: [...challenge.requirements.technique]
    },
    steps: challenge.steps.map(step => ({
      ...step,
      tips: [...step.tips]
    }))
  }), [challenge]);

  const [formData, setFormData] = useState<Omit<Challenge, 'id'>>(initialFormData);

  // Reset form only when challenge changes, not on every modal open
  useEffect(() => {
    // Skip the first render
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    // Only reset if the challenge has actually changed
    if (challenge.id !== previousChallengeRef.current.id) {
      setFormData(initialFormData);
      setError(null);
      previousChallengeRef.current = challenge;
    }
  }, [challenge, initialFormData]);

  // Clear error when modal closes
  useEffect(() => {
    if (!isOpen) {
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const challengeRef = doc(db, 'challenges', challenge.id);
      
      await updateDoc(challengeRef, {
        title: formData.title,
        description: formData.description,
        requirements: formData.requirements,
        steps: formData.steps
      });

      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error updating challenge:', err);
      setError('Failed to update challenge. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Memoized handlers for form updates with cursor position preservation
  const handleInputChange = useCallback((field: keyof Challenge, value: string, inputRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement>) => {
    if (inputRef?.current) {
      const cursorPosition = inputRef.current.selectionStart;
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
      // Restore cursor position after state update
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.selectionStart = cursorPosition;
          inputRef.current.selectionEnd = cursorPosition;
        }
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  }, []);

  const addArrayItem = useCallback((field: keyof Challenge['requirements']) => {
    setFormData(prev => ({
      ...prev,
      requirements: {
        ...prev.requirements,
        [field]: [...prev.requirements[field], '']
      }
    }));
  }, []);

  const removeArrayItem = useCallback((field: keyof Challenge['requirements'], index: number) => {
    setFormData(prev => ({
      ...prev,
      requirements: {
        ...prev.requirements,
        [field]: prev.requirements[field].filter((_, i) => i !== index)
      }
    }));
  }, []);

  const updateArrayItem = useCallback((field: keyof Challenge['requirements'], index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      requirements: {
        ...prev.requirements,
        [field]: prev.requirements[field].map((item, i) => 
          i === index ? value : item
        )
      }
    }));
  }, []);

  const addStep = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      steps: [
        ...prev.steps,
        {
          id: (prev.steps.length + 1).toString(),
          title: '',
          description: '',
          tips: ['']
        }
      ]
    }));
  }, []);

  const removeStep = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
  }, []);

  const updateStep = useCallback((index: number, field: keyof Challenge['steps'][0], value: string) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) =>
        i === index ? { ...step, [field]: value } : step
      )
    }));
  }, []);

  const addStepTip = useCallback((stepIndex: number) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => {
        if (i === stepIndex) {
          return { ...step, tips: [...step.tips, ''] };
        }
        return step;
      })
    }));
  }, []);

  const removeStepTip = useCallback((stepIndex: number, tipIndex: number) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => {
        if (i === stepIndex) {
          return {
            ...step,
            tips: step.tips.filter((_, j) => j !== tipIndex)
          };
        }
        return step;
      })
    }));
  }, []);

  const updateStepTip = useCallback((stepIndex: number, tipIndex: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => {
        if (i === stepIndex) {
          const newTips = [...step.tips];
          newTips[tipIndex] = value;
          return { ...step, tips: newTips };
        }
        return step;
      })
    }));
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[400] p-4">
      <div className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl text-white font-brand font-light italic">Edit Challenge</h2>
            <button 
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-500/10 backdrop-blur-sm text-red-200 rounded-xl text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-white/90 mb-1">
              Challenge Title
            </label>
            <input
              ref={titleInputRef}
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value, titleInputRef)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/90 mb-1">
              Description
            </label>
            <textarea
              ref={descriptionInputRef}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value, descriptionInputRef)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20 min-h-[100px]"
              required
            />
          </div>

          {/* Requirements Section */}
          <div className="space-y-4">
            <h3 className="font-medium text-white">Requirements</h3>

            {/* Flour Requirements */}
            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">
                Required Flour Types
              </label>
              {formData.requirements.flour.map((flour, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={flour}
                    onChange={(e) => updateArrayItem('flour', index, e.target.value)}
                    className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
                    placeholder="e.g., Whole Wheat Flour"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayItem('flour', index)}
                    className="p-2 text-red-300 hover:text-red-200 hover:bg-red-500/20 rounded-xl"
                  >
                    <Trash className="w-5 h-5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('flour')}
                className="text-sm text-amber-400 hover:text-amber-300 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Flour Type
              </button>
            </div>

            {/* Equipment Requirements */}
            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">
                Required Equipment
              </label>
              {formData.requirements.equipment.map((equipment, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={equipment}
                    onChange={(e) => updateArrayItem('equipment', index, e.target.value)}
                    className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
                    placeholder="e.g., Dutch Oven"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayItem('equipment', index)}
                    className="p-2 text-red-300 hover:text-red-200 hover:bg-red-500/20 rounded-xl"
                  >
                    <Trash className="w-5 h-5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('equipment')}
                className="text-sm text-amber-400 hover:text-amber-300 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Equipment
              </button>
            </div>

            {/* Technique Requirements */}
            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">
                Required Techniques
              </label>
              {formData.requirements.technique.map((technique, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={technique}
                    onChange={(e) => updateArrayItem('technique', index, e.target.value)}
                    className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
                    placeholder="e.g., Stretch and Fold"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayItem('technique', index)}
                    className="p-2 text-red-300 hover:text-red-200 hover:bg-red-500/20 rounded-xl"
                  >
                    <Trash className="w-5 h-5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('technique')}
                className="text-sm text-amber-400 hover:text-amber-300 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Technique
              </button>
            </div>
          </div>

          {/* Steps Section */}
          <div className="space-y-4">
            <h3 className="font-medium text-white">Challenge Steps</h3>
            {formData.steps.map((step, index) => (
              <div key={step.id} className="p-4 bg-white/10 rounded-xl space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium text-white">Step {index + 1}</h4>
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => removeStep(index)}
                      className="p-2 text-red-300 hover:text-red-200 hover:bg-red-500/20 rounded-xl"
                    >
                      <Trash className="w-5 h-5" />
                    </button>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/90 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={step.title}
                    onChange={(e) => updateStep(index, 'title', e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/90 mb-1">
                    Description
                  </label>
                  <textarea
                    value={step.description}
                    onChange={(e) => updateStep(index, 'description', e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20 min-h-[100px]"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/90 mb-1">
                    Tips
                  </label>
                  {step.tips.map((tip, tipIndex) => (
                    <div key={tipIndex} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={tip}
                        onChange={(e) => updateStepTip(index, tipIndex, e.target.value)}
                        className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
                        placeholder="Add a helpful tip"
                        required
                      />
                      {tipIndex > 0 && (
                        <button
                          type="button"
                          onClick={() => removeStepTip(index, tipIndex)}
                          className="p-2 text-red-300 hover:text-red-200 hover:bg-red-500/20 rounded-xl"
                        >
                          <Trash className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addStepTip(index)}
                    className="text-sm text-amber-400 hover:text-amber-300 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Tip
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addStep}
              className="w-full py-3 border-2 border-dashed border-white/20 rounded-xl text-white/70 hover:border-amber-400 hover:text-amber-400 flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Step
            </button>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-white/20 rounded-xl hover:bg-white/10 text-white transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-amber-500/90 to-amber-600/90 hover:from-amber-500 hover:to-amber-600 text-white rounded-xl transition-all duration-300 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}